import { create } from 'zustand'
import { getProspectPreview, startProspectPreview } from '../services/api.js'

const POLL_MS = 1500
// Coupures réseau tolérées d'affilée pendant le suivi avant d'abandonner (la recherche continue côté serveur).
const MAX_POLL_ERRORS = 5

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

function errorMessage(err) {
  if (err?.response?.status === 402) return 'Crédits insuffisants pour lancer une recherche (1 crédit).'
  if (err?.response?.status === 429) return 'Trop de recherches d’affilée : réessayez dans une minute.'
  return err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Erreur de recherche'
}

function toResults(state, query) {
  return {
    prospects: state.preview || [],
    total: state.preview?.length || 0,
    requestId: state.requestId,
    availableContactIds: state.availableContactIds || [],
    warnings: state.warnings || [],
    query,
  }
}

/**
 * Store global de la recherche B2B. La recherche tourne dans le worker (prospects.search) : on la
 * lance, puis on suit son état ; les entreprises arrivent au fur et à mesure (`found`, `progress`).
 * Le suivi continue même si l'utilisateur quitte la page ; une nouvelle recherche ou reset() l'arrête.
 */
export const useProspectSearchStore = create((set, get) => ({
  loading: false,
  error: null,
  results: null,
  currentQuery: '',
  progress: null, // { current, total, message } envoyé par le worker
  found: [], // entreprises déjà trouvées pendant la recherche
  searchId: 0,
  notified: true, // true = l'utilisateur a déjà vu les résultats ou il n'y en a pas

  startSearch: async (query, quantity) => {
    const searchId = get().searchId + 1
    const isCurrent = () => get().searchId === searchId
    set({ searchId, loading: true, error: null, results: null, currentQuery: query, progress: null, found: [], notified: true })

    try {
      let state = await startProspectPreview(query, quantity)
      let pollErrors = 0
      while (!state.done && state.status !== 'failed') {
        if (!isCurrent()) return
        set({ progress: state.progress, found: state.preview || [] })
        await sleep(POLL_MS)
        if (!isCurrent()) return
        try {
          state = await getProspectPreview(state.jobId)
          pollErrors = 0
        } catch (err) {
          if (++pollErrors >= MAX_POLL_ERRORS) throw err
        }
      }
      if (!isCurrent()) return

      if (state.status === 'failed') {
        set({ loading: false, progress: null, error: `${state.error} Votre crédit a été rendu.` })
        return
      }
      if (!state.preview?.length) {
        set({ loading: false, progress: null, error: 'Aucune entreprise ne correspond à cette recherche. Élargissez la zone ou le secteur.' })
        return
      }
      set({ loading: false, progress: null, found: [], results: toResults(state, query), notified: false })
    } catch (err) {
      if (!isCurrent()) return
      set({ loading: false, progress: null, error: errorMessage(err), results: null })
    }
  },

  setResults: (results) => set({ results, loading: false, notified: true }),

  markNotified: () => set({ notified: true }),

  reset: () =>
    set((s) => ({
      searchId: s.searchId + 1, // arrête le suivi d'une recherche en cours
      loading: false,
      error: null,
      results: null,
      currentQuery: '',
      progress: null,
      found: [],
      notified: true,
    })),
}))
